// Practice taxonomy — the onboarding picker's specialty and country catalogue, and the
// one place that turns a STORED value into a label in the reader's language.
//
// THE PROBLEM THIS SOLVES (BUG-218). The wizard's option lists used to carry the French
// display label as the option VALUE, and the Step-4 summary echoed the stored value
// verbatim — so an English or Spanish dashboard could only ever print "Naturopathie" and
// "États-Unis". Here the two roles are separated: `stored` is the wire/DB value, `code` is
// a locale-neutral key, and the label is looked up per render.
//
// `stored` IS FROZEN, BYTE FOR BYTE. It is what `tenants.specialty` / `tenants.country`
// already hold for every existing tenant and what the wizard keeps sending to
// /tenant/onboarding/complete, because that field is rendered raw by readers this module
// cannot reach — all of them Python:
//   backend/emails/shared.py:117      practice subtitle in every tenant-branded e-mail
//   backend/modules/booking/side_effects.py:1299  the .ics / calendar invite summary
//   backend/calendar_helper.py:120    the identity that feeds the line above
//   backend/modules/social.py:125     LLM system prompt
//   backend/modules/ai_agent.py:86    LLM system prompt
//   frontend/src/components/site/Hero.jsx:21     public-site credential badge
// Re-wording an entry below therefore does NOT rename a label — it strands every row that
// holds the old string and changes what those six surfaces print. Translate the DICTIONARY
// (dictionaries/admin/onboarding.js), never these values.
//
// NOT THE SAME THING AS `backend/modality_packs.py`, which also has `naturopathy`,
// `osteopathy` and `acupuncture` slugs (MODALITY_PACK_TEMPLATES, modality_packs.py:41).
// Those five slugs identify a CLINICAL INTAKE PACK — intake_fields, taxonomy, protocols and
// ai_constraints, instantiated per tenant as an editable snapshot in the `modality_packs`
// collection, several per tenant. These entries are a single DISPLAY ATTRIBUTE of the
// practice (`tenants.specialty`, one per tenant, printed on the public site and in e-mail).
// The sets are different sizes on purpose (there is no intake pack for massage therapy,
// reflexology or "Other"), so do not "unify" them: driving intake content off the signup
// picker would leave eight of these eleven specialties with no intake form at all.
// A THIRD, also distinct list is `admin/branding/SpecialtiesEditor.jsx` — the tenant's
// `branding.specialties` ARRAY, which is what the marketplace filters on
// (telehealth_marketplace_routes.py:444). `tenants.specialty` is not that field.
//
// The label TEXT lives once, in `dictionaries/admin/onboarding.js` (fr/en/es), under
// `onboarding.specialty.<code>` / `onboarding.country.<code>` — this module restates no
// copy. Surfaces holding an "admin"-bound `t` use `labelKey`; the rest use
// `specialtyLabel`/`countryLabel`.
import { lookup } from "./index";

/** [{ code, stored }] in display order. `stored` = frozen DB/wire value. */
const SPECIALTY_ENTRIES = [
  { code: "naturopathy", stored: "Naturopathie" }, // i18n-ignore
  { code: "osteopathy", stored: "Ostéopathie" }, // i18n-ignore
  { code: "acupuncture", stored: "Acupuncture" }, // i18n-ignore
  { code: "kinesiology", stored: "Kinésiologie" }, // i18n-ignore
  { code: "massage_therapy", stored: "Massothérapie" }, // i18n-ignore
  { code: "functional_medicine", stored: "Médecine fonctionnelle" }, // i18n-ignore
  { code: "holistic_nutrition", stored: "Nutrition holistique" }, // i18n-ignore
  { code: "reflexology", stored: "Réflexologie" }, // i18n-ignore
  { code: "homeopathy", stored: "Homéopathie" }, // i18n-ignore
  { code: "hypnotherapy", stored: "Hypnothérapie" }, // i18n-ignore
  { code: "other", stored: "Autre" }, // i18n-ignore
];

const COUNTRY_ENTRIES = [
  { code: "canada", stored: "Canada" }, // i18n-ignore
  { code: "united_states", stored: "États-Unis" }, // i18n-ignore
  { code: "france", stored: "France" }, // i18n-ignore
  { code: "belgium", stored: "Belgique" }, // i18n-ignore
  { code: "switzerland", stored: "Suisse" }, // i18n-ignore
  { code: "united_kingdom", stored: "Royaume-Uni" }, // i18n-ignore
  { code: "morocco", stored: "Maroc" }, // i18n-ignore
  { code: "senegal", stored: "Sénégal" }, // i18n-ignore
  { code: "india", stored: "Inde" }, // i18n-ignore
  { code: "other", stored: "Autre" }, // i18n-ignore
];

const withKeys = (entries, keyPrefix) =>
  entries.map((e) => ({ ...e, labelKey: `${keyPrefix}.${e.code}` }));

/** Option lists for the picker. `labelKey` is an "admin" namespace key. */
export const SPECIALTIES = withKeys(SPECIALTY_ENTRIES, "onboarding.specialty");
export const COUNTRIES = withKeys(COUNTRY_ENTRIES, "onboarding.country");

/**
 * Resolve a STORED value to `{ stored, labelKey, text }`.
 *
 * Three input classes:
 *   - a catalogue value ("Naturopathie")  -> its labelKey, so the caller can localize;
 *   - a catalogue CODE ("naturopathy")    -> the same labelKey. Accepted so that migrating
 *     the column to codes later is a DATA change and not a change to every renderer;
 *   - anything else                       -> { labelKey: null, text: <input> }. That is a
 *     designed class, not a fallback: `tenants.specialty` is `Optional[str]` free text
 *     (saas_models.py:463) with writers other than this wizard, so a value outside the
 *     catalogue is the practitioner's own words and is carried through VERBATIM rather
 *     than blanked or shown as a raw key.
 */
const resolve = (value, options) => {
  const raw = (value || "").trim();
  if (!raw) return { stored: "", labelKey: null, text: "" };
  const hit = options.find((o) => o.stored === raw) || options.find((o) => o.code === raw);
  if (!hit) return { stored: raw, labelKey: null, text: raw };
  return { stored: raw, labelKey: hit.labelKey, text: null };
};

export const resolveSpecialty = (value) => resolve(value, SPECIALTIES);
export const resolveCountry = (value) => resolve(value, COUNTRIES);

/** Display label for a stored value in `lang`; free text is returned as it was stored. */
export const specialtyLabel = (value, lang) => {
  const r = resolveSpecialty(value);
  return r.labelKey ? lookup("admin", r.labelKey, lang) : r.text || "";
};

export const countryLabel = (value, lang) => {
  const r = resolveCountry(value);
  return r.labelKey ? lookup("admin", r.labelKey, lang) : r.text || "";
};
